// A part's board components on its own panel (Mitch, 2026-11-02: "If I don't have the BOM, I can't see the components
// list"): any part whose spec keeps a KiCad project shows the components read from its schematics (BoardTab.tsx's
// useBoard: references, value, footprint, part number), whether or not it has a BOM, in the usual table (views, columns,
// Copy / CSV), and the way to its BOM's Board tab, where who supplies each one is set (or to start its BOM).
import { ArrowRight, Cpu } from "lucide-react";
import { useMemo, useState } from "react";
import type { BoardRow } from "../../../shared/board-bom.ts";
import type { Item } from "../../../shared/db.gen.ts";
import { bomLink, bomPlaceFor, createBom, currentBomOf, familyOfItem, useBomIndex } from "../lib/boms.ts";
import { qty } from "../lib/format.ts";
import { can } from "../lib/mutate.ts";
import { Link } from "../lib/router.tsx";
import { type Column, DataTable } from "../ui/DataTable.tsx";
import { Section } from "../ui/RecordPanel.tsx";
import { useBoard, useBoardProject } from "./BoardTab.tsx";

type Row = BoardRow & { id: string; order: number };

const truncated = (text: string, className?: string) => (
  <span className={className ? `truncate ${className}` : "truncate"} title={text}>
    {text}
  </span>
);

// The columns of the CSV this panel had (References, Qty, Value, Footprint, Manufacturer, Part number), in its order.
const columns: Column<Row>[] = [
  {
    key: "refs",
    header: "References",
    width: "minmax(120px,1.2fr)",
    value: (r) => r.order, // the board's own order
    text: (r) => r.refs.join(" "),
    cell: (r) => (
      <span className="truncate font-mono text-[12px]" title={[r.refs.join(", "), r.footprint, r.manufacturer, r.description].filter(Boolean).join(" · ")}>
        {r.refs.join(", ")}
      </span>
    ),
  },
  { key: "qty", header: "Qty", width: "56px", align: "right", cell: (r) => <span className="font-mono">{qty(r.qty)}</span> },
  { key: "value", header: "Value", width: "minmax(100px,1fr)", cell: (r) => truncated(r.value) },
  { key: "footprint", header: "Footprint", width: "minmax(110px,1fr)", cell: (r) => truncated(r.footprint, "text-muted") },
  { key: "manufacturer", header: "Manufacturer", width: "minmax(100px,1fr)", cell: (r) => truncated(r.manufacturer, "text-muted") },
  {
    key: "mpn",
    header: "Part number",
    width: "minmax(110px,1.2fr)",
    cell: (r) => (r.mpn ? truncated(r.mpn, "font-mono text-muted") : <span className="text-faint">—</span>),
  },
  { key: "description", header: "Description", width: "minmax(140px,1.5fr)", optional: true, cell: (r) => truncated(r.description, "text-muted") },
];

export function BoardSection({ item }: { item: Item }) {
  const project = useBoardProject(item.id);
  const loaded = useBoard(project?.id);
  const ix = useBomIndex();
  const board = loaded?.board;
  const rows = useMemo<Row[]>(() => (board?.rows ?? []).map((r, order) => ({ ...r, id: r.refs.join(","), order })), [board]);
  if (!project) return null;
  const family = familyOfItem(item);
  const bom = currentBomOf(ix, family);
  const place = bomPlaceFor(location.pathname);
  const parts = rows.reduce((n, r) => n + r.qty, 0);
  return (
    <Section title={board ? `Board · ${qty(parts)} components` : "Board"}>
      <div className="px-2 pb-2 text-[12px]">
        <div className="mb-1.5 flex flex-wrap items-center gap-2 text-muted">
          <Cpu className="size-3.5" />
          <span className="min-w-0 truncate">From {project.filename}</span>
        </div>
        {loaded?.error && <div className="text-warn">Couldn't read the board: {loaded.error}</div>}
        {!loaded && <div className="text-faint">Reading the schematics…</div>}
        {board && (
          <>
            <DataTable
              fit
              rows={rows}
              columns={columns}
              defaultSort={{ key: "refs" }}
              rowNoun={["row", "rows"]}
              exportName="Part board"
              pageFilters={false}
              empty={<div className="p-6 text-center text-muted">The schematics have no parts.</div>}
            />
            {board.dnp.length > 0 && <div className="mt-1 text-faint">Not fitted: {board.dnp.join(", ")}</div>}
          </>
        )}
        <div className="mt-2">
          {bom ? (
            <Link to={`${bomLink(bom.id)}&${place.tab}=board`} className="inline-flex items-center gap-1 text-accent-text hover:underline">
              Who supplies each one: its BOM's Board tab <ArrowRight className="size-3" />
            </Link>
          ) : item.kind === "subassembly" && can("bom.create") ? (
            <button type="button" className="text-accent-text hover:underline" onClick={() => createBom(ix, { item_id: item.id })}>
              Start its BOM to say who supplies each one
            </button>
          ) : (
            <span className="text-faint">Who supplies each one is set on the BOM of what it's built into.</span>
          )}
        </div>
      </div>
    </Section>
  );
}
